import { baseApi } from 'shared/api/baseApi'
import { CMS_PUBLICATION_CATEGORIES_TAG } from 'shared/api/tags'
import type {
    CMSCreatePublicationCategoryRequest,
    CMSDeletePublicationCategoryRequest,
    CMSPublicationCategory,
    CMSPublicationCategoryListParams,
    CMSReorderPublicationCategoriesRequest,
    CMSUpdatePublicationCategoryRequest,
} from '../model/types'

export const cmsPublicationCategoriesApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getCmsPublicationCategories: build.query<
            CMSPublicationCategory[],
            CMSPublicationCategoryListParams
        >({
            query: ({ organizationId }) => ({
                url: `organizations/${organizationId}/cms-publication-categories`,
                method: 'GET',
            }),
            providesTags: [CMS_PUBLICATION_CATEGORIES_TAG],
        }),

        createCmsPublicationCategory: build.mutation<
            CMSPublicationCategory,
            CMSCreatePublicationCategoryRequest
        >({
            query: ({ organizationId, ...body }) => ({
                url: `organizations/${organizationId}/cms-publication-categories`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [CMS_PUBLICATION_CATEGORIES_TAG],
        }),

        updateCmsPublicationCategory: build.mutation<
            CMSPublicationCategory,
            CMSUpdatePublicationCategoryRequest
        >({
            query: ({ organizationId, categoryId, data }) => ({
                url: `organizations/${organizationId}/cms-publication-categories/${categoryId}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: [CMS_PUBLICATION_CATEGORIES_TAG],
        }),

        reorderCmsPublicationCategories: build.mutation<
            CMSPublicationCategory[],
            CMSReorderPublicationCategoriesRequest
        >({
            query: ({ organizationId, ids }) => ({
                url: `organizations/${organizationId}/cms-publication-categories/order`,
                method: 'PUT',
                body: { ids },
            }),
            // Show the new order right away and roll it back if saving fails.
            async onQueryStarted(
                { organizationId, ids },
                { dispatch, queryFulfilled }
            ) {
                const patch = dispatch(
                    cmsPublicationCategoriesApi.util.updateQueryData(
                        'getCmsPublicationCategories',
                        { organizationId },
                        (draft) => {
                            const position = new Map(
                                ids.map((id, index) => [id, index])
                            )
                            draft.sort(
                                (a, b) =>
                                    (position.get(a.id) ?? draft.length) -
                                    (position.get(b.id) ?? draft.length)
                            )
                        }
                    )
                )
                try {
                    await queryFulfilled
                } catch {
                    patch.undo()
                }
            },
            invalidatesTags: [CMS_PUBLICATION_CATEGORIES_TAG],
        }),

        deleteCmsPublicationCategories: build.mutation<
            void,
            CMSDeletePublicationCategoryRequest
        >({
            query: ({ organizationId, ids }) => ({
                url: `organizations/${organizationId}/cms-publication-categories?ids=${ids.join(',')}`,
                method: 'DELETE',
            }),
            invalidatesTags: [CMS_PUBLICATION_CATEGORIES_TAG],
        }),
    }),
})

export const {
    useGetCmsPublicationCategoriesQuery,
    useCreateCmsPublicationCategoryMutation,
    useUpdateCmsPublicationCategoryMutation,
    useDeleteCmsPublicationCategoriesMutation,
    useReorderCmsPublicationCategoriesMutation,
} = cmsPublicationCategoriesApi
